import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  useToast,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  getCancelPreview,
  postCancelBooking,
  type BookingType,
  type CancelPreviewResponse,
} from '../../api/user/bookings/Cancel'

interface CancelBookingModalProps {
  isOpen: boolean
  onClose: () => void
  bookingId: string | null
  bookingType: BookingType
}

const formatUsd = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const CancelBookingModal = ({ isOpen, onClose, bookingId, bookingType }: CancelBookingModalProps) => {
  const toast = useToast()
  const queryClient = useQueryClient()

  const previewQuery = useQuery<CancelPreviewResponse>(
    ['cancelPreview', bookingType, bookingId],
    () => getCancelPreview(bookingType, bookingId as string),
    { enabled: !!bookingId && isOpen, staleTime: 30 * 1000 },
  )

  const cancelMutation = useMutation(() => postCancelBooking(bookingType, bookingId as string), {
    onSuccess: (data) => {
      toast({
        title: `Booking cancelled — ${formatUsd(data.refund_cents)} refund on its way`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      if (bookingType === 'lesson') {
        queryClient.invalidateQueries('getUpcomingLessonBookings')
      } else {
        queryClient.invalidateQueries('upcomingCourtRentals')
      }
      onClose()
    },
    onError: () => {
      toast({
        title: 'Cancel failed — please try again or contact support',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    },
  })

  const handleClose = () => {
    if (cancelMutation.isLoading) return
    onClose()
  }

  const preview = previewQuery.data

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Cancel booking</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {previewQuery.isLoading ? (
            <Skeleton height="60px" />
          ) : preview && 'closed' in preview ? (
            <Text textStyle="medium.300">
              Booking has already started — contact the venue for assistance.
            </Text>
          ) : preview && 'already_cancelled' in preview ? (
            <Text textStyle="medium.300">
              Already cancelled — refund {formatUsd(preview.refund_cents)} processed.
            </Text>
          ) : preview ? (
            <>
              <Text textStyle="medium.300">
                Cancel now and receive <b>{formatUsd(preview.refund_cents)}</b> ({preview.refund_pct}% per
                current policy, slot starts in {preview.hours_before_start.toFixed(0)} hours).
              </Text>
              {bookingType === 'lesson' && (
                <Text textStyle="light.300" color="#7E8B9A" mt="2">
                  Your coach will be notified
                </Text>
              )}
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" mr={3} onClick={handleClose} isDisabled={cancelMutation.isLoading}>
            Keep booking
          </Button>
          <Button
            variant="heavy"
            onClick={() => cancelMutation.mutate()}
            isLoading={cancelMutation.isLoading}
            isDisabled={!preview || 'closed' in preview || 'already_cancelled' in preview}
          >
            Confirm cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default CancelBookingModal
